import { useMemo, useState } from 'react'
import {
  Box, Chip, CircularProgress, Divider, IconButton, MenuItem, Select, Stack, Tab, Tabs, Tooltip, Typography,
} from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { PieChart } from '@mui/x-charts/PieChart'
import { SparkLineChart } from '@mui/x-charts/SparkLineChart'
import { gaugeClasses } from '@mui/x-charts/Gauge'
import { Gauge } from '@mui/x-charts/Gauge'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined'
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined'
import TrendingFlatOutlinedIcon from '@mui/icons-material/TrendingFlatOutlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import ArrowOutwardOutlinedIcon from '@mui/icons-material/ArrowOutwardOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  MttrMtbfData, PmComplianceData, SlaComplianceData,
  CostAnalysisData, BacklogData, WoVolumeData,
} from '@cmms/api-client'

import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { useOrgSettings } from '../../hooks/useOrgSettings'

type TabKey = 'overview' | 'mttr-mtbf' | 'pm' | 'sla' | 'cost' | 'backlog'

const PRIORITY_COLORS: Record<string, string> = {
  low: '#22C55E', medium: '#F59E0B', high: '#F97316', critical: '#EF4444',
}

// Premium palette — used across every chart so the page reads as a
// single product surface rather than six unrelated tabs.
const ACCENT = {
  primary:  '#0D9488', // teal — house brand
  blue:     '#3B82F6',
  violet:   '#7C3AED',
  green:    '#059669',
  amber:    '#F59E0B',
  red:      '#DC2626',
  slate:    '#64748B',
}

export function ReportsPage() {
  const [tab, setTab] = useState<TabKey>('overview')
  const [months, setMonths] = useState(6)
  const { prefs } = useOrgSettings()
  const currency = prefs.currencySymbol ?? '$'
  const qc = useQueryClient()

  const tabSx = {
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40, textTransform: 'none', fontSize: 13.5, fontWeight: 600,
      px: 0, mr: 3.5, color: 'text.secondary',
      '&.Mui-selected': { color: ACCENT.primary },
    },
    '& .MuiTabs-indicator': { height: 2.5, borderRadius: 2, backgroundColor: ACCENT.primary },
  }

  const toolbar = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ ...tabSx, flex: 1 }}>
        <Tab value="overview"  icon={<TrendingUpOutlinedIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Work Volume" />
        <Tab value="mttr-mtbf" icon={<SpeedOutlinedIcon sx={{ fontSize: 17 }} />}      iconPosition="start" label="MTTR / MTBF" />
        <Tab value="pm"        icon={<BuildOutlinedIcon sx={{ fontSize: 17 }} />}      iconPosition="start" label="PM Compliance" />
        <Tab value="sla"       icon={<TimerOutlinedIcon sx={{ fontSize: 17 }} />}      iconPosition="start" label="SLA" />
        <Tab value="cost"      icon={<AttachMoneyOutlinedIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Cost Analysis" />
        <Tab value="backlog"   icon={<AssignmentOutlinedIcon sx={{ fontSize: 17 }} />}  iconPosition="start" label="Backlog" />
      </Tabs>
      <Tooltip title="Refresh report data" arrow>
        <IconButton size="small"
          onClick={() => qc.invalidateQueries({ queryKey: ['reports'] })}
          sx={{ color: 'text.secondary' }}>
          <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Select value={months} onChange={(e) => setMonths(Number(e.target.value))} size="small"
        sx={{ fontSize: 13, minWidth: 130, fontWeight: 600 }}>
        <MenuItem value={3}>Last 3 months</MenuItem>
        <MenuItem value={6}>Last 6 months</MenuItem>
        <MenuItem value={12}>Last 12 months</MenuItem>
      </Select>
    </Stack>
  )

  return (
    <PageShell title="Reports" toolbar={toolbar}>
      {tab === 'overview'  && <WoVolumeTab   months={months} />}
      {tab === 'mttr-mtbf' && <MttrMtbfTab   months={months} />}
      {tab === 'pm'        && <PmComplianceTab months={months} />}
      {tab === 'sla'       && <SlaComplianceTab months={months} />}
      {tab === 'cost'      && <CostTab       months={months} currency={currency} />}
      {tab === 'backlog'   && <BacklogTab />}
    </PageShell>
  )
}

// ─── Premium hero KPI card ──────────────────────────────────────────────────
//
// The headline numbers at the top of every tab — bigger, gradient-tinted,
// with a sparkline of the underlying series and a period-over-period
// delta chip ("+12% vs prior period"). This is the single biggest visual
// upgrade on the page; everything else is supporting structure.
function HeroKpi({
  label, value, sub, accent = ACCENT.primary, spark, deltaPct, deltaInversed, icon,
}: {
  label: string
  value: string | number
  sub?: string
  accent?: string
  spark?: number[]
  /** Percent change vs previous period; null hides the chip. */
  deltaPct?: number | null
  /** Set to true when "down" is good (cost / overdue / breach count). */
  deltaInversed?: boolean
  icon?: React.ReactNode
}) {
  // Direction logic: deltaInversed=true → "down is green, up is red"
  const trend = (deltaPct ?? 0) > 0.5 ? 'up' : (deltaPct ?? 0) < -0.5 ? 'down' : 'flat'
  const isGood = deltaInversed ? trend === 'down' : trend === 'up'
  const isBad  = deltaInversed ? trend === 'up'   : trend === 'down'
  const trendColor = trend === 'flat' ? ACCENT.slate : isGood ? ACCENT.green : isBad ? ACCENT.red : ACCENT.slate
  const TrendIcon = trend === 'flat' ? TrendingFlatOutlinedIcon : trend === 'up' ? TrendingUpOutlinedIcon : TrendingDownOutlinedIcon

  return (
    <Box sx={{
      flex: 1, minWidth: 200, position: 'relative', overflow: 'hidden',
      p: 2.75, borderRadius: 3,
      border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
      transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: (t) => t.palette.mode === 'light'
          ? `0 18px 30px -16px ${accent}33`
          : '0 18px 30px -16px rgba(0,0,0,0.5)',
        borderColor: `${accent}55`,
      },
      '&::before': {
        content: '""', position: 'absolute', inset: 0,
        background: `linear-gradient(135deg, ${accent}11 0%, transparent 55%)`,
        pointerEvents: 'none',
      },
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ position: 'relative', mb: 1.25 }}>
        {icon && (
          <Box sx={{
            width: 28, height: 28, borderRadius: 1.25,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${accent}1F`, color: accent,
          }}>
            {icon}
          </Box>
        )}
        <Typography sx={{
          fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.1em', color: 'text.secondary',
        }}>
          {label}
        </Typography>
      </Stack>

      <Typography sx={{
        position: 'relative',
        fontSize: 32, fontWeight: 800, letterSpacing: '-0.035em',
        lineHeight: 1.05, color: 'text.primary',
      }}>
        {value}
      </Typography>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}
        sx={{ position: 'relative', mt: 1.25 }}>
        <Stack direction="row" alignItems="center" spacing={0.85}>
          {deltaPct != null && (
            <Chip
              size="small"
              icon={<TrendIcon sx={{ fontSize: 13, color: `${trendColor} !important` }} />}
              label={`${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(1)}%`}
              sx={{
                height: 20, fontSize: 11, fontWeight: 700,
                bgcolor: `${trendColor}1A`, color: trendColor,
                '& .MuiChip-icon': { ml: '6px !important', mr: '-2px' },
              }}
            />
          )}
          {sub && (
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.2 }}>
              {sub}
            </Typography>
          )}
        </Stack>

        {spark && spark.length > 1 && (
          <Box sx={{ width: 88, height: 28, opacity: 0.95 }}>
            <SparkLineChart
              data={spark}
              height={28} width={88}
              showHighlight showTooltip
              color={accent}
              area
              sx={{
                '& .MuiAreaElement-root': { fillOpacity: 0.18 },
                '& .MuiLineElement-root': { strokeWidth: 1.8 },
              }}
            />
          </Box>
        )}
      </Stack>
    </Box>
  )
}

// ─── Section card ────────────────────────────────────────────────────────────
// Soft-shadow card used for every chart / list block. Headline + optional
// subtitle + optional right-side action (filter chip / link).
function Section({
  title, subtitle, action, children, accent,
}: {
  title?: React.ReactNode
  subtitle?: React.ReactNode
  action?: React.ReactNode
  children: React.ReactNode
  accent?: string
}) {
  return (
    <Box sx={{
      p: 3, borderRadius: 3,
      border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
      position: 'relative',
      transition: 'box-shadow 200ms ease',
      '&:hover': {
        boxShadow: (t) => t.palette.mode === 'light'
          ? '0 6px 16px -6px rgba(15,23,42,0.08)' : 'none',
      },
      ...(accent && {
        '&::before': {
          content: '""', position: 'absolute', left: 0, top: 18, bottom: 18,
          width: 3, borderRadius: 2, bgcolor: accent,
        },
      }),
    }}>
      {(title || action) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            {title && (
              <Typography sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-0.01em' }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
      )}
      {children}
    </Box>
  )
}

// Inline insight strip — derived narrative observations about the data.
// Acts like an executive summary so a CEO can scan one paragraph and
// know whether they need to dig into the charts.
function InsightBar({ items }: { items: { tone: 'good' | 'warn' | 'info'; text: string }[] }) {
  if (items.length === 0) return null
  const TONE = {
    good: { bg: '#05966915', color: '#059669', icon: <WorkspacePremiumOutlinedIcon sx={{ fontSize: 15 }} /> },
    warn: { bg: '#F59E0B15', color: '#B45309', icon: <WarningAmberOutlinedIcon sx={{ fontSize: 15 }} /> },
    info: { bg: '#3B82F615', color: '#1D4ED8', icon: <InsightsOutlinedIcon sx={{ fontSize: 15 }} /> },
  } as const
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
      {items.map((it, i) => {
        const t = TONE[it.tone]
        return (
          <Stack key={i} direction="row" alignItems="center" spacing={1}
            sx={{
              flex: 1, px: 1.75, py: 1.25, borderRadius: 2.5,
              bgcolor: t.bg, color: t.color,
              border: '1px solid', borderColor: `${t.color}22`,
            }}>
            <Box sx={{ display: 'flex', flexShrink: 0 }}>{t.icon}</Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}>
              {it.text}
            </Typography>
          </Stack>
        )
      })}
    </Stack>
  )
}

function Loading() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 1.5 }}>
      <CircularProgress size={28} sx={{ color: ACCENT.primary }} />
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Crunching the numbers…</Typography>
    </Box>
  )
}

/** Surfaces backend failures so the page never sits stuck on
 *  "loading" when the query has actually errored. The previous
 *  guard `q.isLoading || !d` swallowed errors — `isLoading` is
 *  false after a failed request, but `!d` was still true, so the
 *  spinner stayed up forever. */
function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const msg = error instanceof Error ? error.message : String(error)
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}>
      <WarningAmberOutlinedIcon sx={{ fontSize: 32, color: ACCENT.amber }} />
      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Couldn't load this report</Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', maxWidth: 520, textAlign: 'center' }}>
        {msg || 'The backend returned an unexpected response.'}
      </Typography>
      <Box component="button" onClick={onRetry}
        sx={{
          mt: 1, px: 2.5, py: 1, borderRadius: 99, cursor: 'pointer',
          border: '1px solid', borderColor: 'divider', bgcolor: 'transparent',
          fontSize: 12.5, fontWeight: 700, color: 'text.primary',
          '&:hover': { bgcolor: 'action.hover' },
        }}>
        Try again
      </Box>
    </Stack>
  )
}

function EmptyState({ icon, title, hint }: { icon?: React.ReactNode; title: string; hint?: string }) {
  return (
    <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
      {icon ?? <InsightsOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled' }} />}
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.secondary' }}>{title}</Typography>
      {hint && <Typography sx={{ fontSize: 12, color: 'text.disabled', maxWidth: 360, textAlign: 'center' }}>{hint}</Typography>}
    </Stack>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Period-over-period change between the second and first halves of an
 *  array of monthly values. Returns null when the data is too short for
 *  a meaningful comparison. */
function periodDelta(values: number[]): number | null {
  if (values.length < 2) return null
  const mid = Math.floor(values.length / 2)
  const prior = values.slice(0, mid)
  const recent = values.slice(mid)
  if (prior.length === 0 || recent.length === 0) return null
  const sum = (arr: number[]) => arr.reduce((s, v) => s + v, 0)
  const a = sum(prior) / prior.length
  const b = sum(recent) / recent.length
  if (a === 0) return b === 0 ? 0 : 100
  return ((b - a) / a) * 100
}

// ─── WO Volume ──────────────────────────────────────────────────────────────

function WoVolumeTab({ months }: { months: number }) {
  const q = useQuery({ queryKey: ['reports', 'wo-volume', months], queryFn: () => api.reports.woVolume({ months }) })
  const d: WoVolumeData | undefined = q.data

  const computed = useMemo(() => {
    if (!d) return null
    const created   = d.by_month.map(m => m.created)
    const completed = d.by_month.map(m => m.completed)
    const totalC = created.reduce((s, v) => s + v, 0)
    const totalK = completed.reduce((s, v) => s + v, 0)
    const completionRate = totalC > 0 ? Math.round((totalK / totalC) * 100) : 0
    const avgPerMonth = d.by_month.length > 0 ? Math.round(totalC / d.by_month.length) : 0
    const peak = d.by_month.reduce((p, m) =>
      (m.created > (p?.created ?? -1) ? m : p), d.by_month[0] ?? null)
    const reactiveCount = d.by_type.find(t => t.work_type === 'reactive')?.count ?? 0
    const preventiveCount = d.by_type.find(t => t.work_type === 'preventive')?.count ?? 0
    const reactiveShare = (reactiveCount + preventiveCount) > 0
      ? Math.round((reactiveCount / (reactiveCount + preventiveCount)) * 100)
      : null
    const criticalShare = d.by_priority.length > 0
      ? Math.round(((d.by_priority.find(p => p.priority === 'critical')?.count ?? 0) /
          d.by_priority.reduce((s, p) => s + p.count, 0)) * 100)
      : null
    return {
      created, completed, totalC, totalK, completionRate, avgPerMonth, peak,
      reactiveShare, criticalShare,
    }
  }, [d])

  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  if (q.isLoading || !d || !computed) return <Loading />

  const insights: { tone: 'good' | 'warn' | 'info'; text: string }[] = []
  if (computed.completionRate >= 90) {
    insights.push({ tone: 'good', text: `Strong throughput — ${computed.completionRate}% of created work is being completed.` })
  } else if (computed.completionRate > 0 && computed.completionRate < 70) {
    insights.push({ tone: 'warn', text: `Backlog risk — only ${computed.completionRate}% of created work is closing in-period.` })
  }
  if (computed.reactiveShare != null && computed.reactiveShare >= 60) {
    insights.push({ tone: 'warn', text: `${computed.reactiveShare}% of work is reactive — consider expanding preventive maintenance.` })
  } else if (computed.reactiveShare != null && computed.reactiveShare <= 30) {
    insights.push({ tone: 'good', text: `Healthy mix — only ${computed.reactiveShare}% of work is reactive.` })
  }
  if (computed.peak) {
    insights.push({ tone: 'info', text: `Peak month was ${computed.peak.month} with ${computed.peak.created} new work orders.` })
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <HeroKpi label="Total created"     value={computed.totalC} icon={<AssignmentOutlinedIcon sx={{ fontSize: 16 }} />}
          accent={ACCENT.blue}    spark={computed.created}   deltaPct={periodDelta(computed.created)} />
        <HeroKpi label="Total completed"   value={computed.totalK} icon={<TrendingUpOutlinedIcon sx={{ fontSize: 16 }} />}
          accent={ACCENT.green}   spark={computed.completed} deltaPct={periodDelta(computed.completed)} />
        <HeroKpi label="Completion rate"   value={`${computed.completionRate}%`} sub="created → completed"
          accent={ACCENT.violet}  deltaPct={null} />
        <HeroKpi label="Avg / month"       value={computed.avgPerMonth} sub="new work orders"
          accent={ACCENT.primary} deltaPct={null} />
      </Stack>

      <InsightBar items={insights} />

      <Section title="Work orders by month" subtitle="Created vs completed — a healthy ops team keeps these stacked close together.">
        {d.by_month.length > 0 ? (
          <BarChart height={320}
            margin={{ left: 50, right: 16, top: 16, bottom: 40 }}
            series={[
              { data: computed.created,   label: 'Created',   color: ACCENT.blue, valueFormatter: (v) => String(v ?? 0) },
              { data: computed.completed, label: 'Completed', color: ACCENT.green, valueFormatter: (v) => String(v ?? 0) },
            ]}
            xAxis={[{ data: d.by_month.map(m => m.month), scaleType: 'band' }]}
            grid={{ horizontal: true }}
            sx={{ '& .MuiChartsAxis-line': { stroke: 'transparent' }, '& .MuiChartsAxis-tick': { stroke: 'transparent' } }}
          />
        ) : <EmptyState title="No work order activity yet" hint="Create a few work orders to start populating this view." />}
      </Section>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
        <Box sx={{ flex: 1 }}>
          <Section title="By work type" subtitle="Reactive, preventive, inspection, safety…">
            {d.by_type.length > 0 ? (
              <PieChart height={240}
                series={[{
                  innerRadius: 56, outerRadius: 96, paddingAngle: 2, cornerRadius: 6,
                  data: d.by_type.map((t, i) => ({
                    id: i, value: t.count,
                    label: t.work_type.replace(/_/g, ' '),
                  })),
                  highlightScope: { fade: 'global', highlight: 'item' },
                }]}
                slotProps={{ legend: { direction: 'horizontal', position: { vertical: 'bottom', horizontal: 'center' } } }}
              />
            ) : <EmptyState title="No work type data" />}
          </Section>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Section title="By priority" subtitle="Mix of urgent vs routine work">
            {d.by_priority.length > 0 ? (
              <PieChart height={240}
                series={[{
                  innerRadius: 56, outerRadius: 96, paddingAngle: 2, cornerRadius: 6,
                  data: d.by_priority.map((p, i) => ({
                    id: i, value: p.count, label: p.priority,
                    color: PRIORITY_COLORS[p.priority] ?? ACCENT.slate,
                  })),
                  highlightScope: { fade: 'global', highlight: 'item' },
                }]}
                slotProps={{ legend: { direction: 'horizontal', position: { vertical: 'bottom', horizontal: 'center' } } }}
              />
            ) : <EmptyState title="No priority data" />}
          </Section>
        </Box>
      </Stack>

      {d.by_site.length > 0 && (
        <Section title="By site" subtitle={`Top ${Math.min(d.by_site.length, 10)} sites by work volume`}>
          <BarChart height={Math.max(240, d.by_site.length * 36)}
            layout="horizontal"
            margin={{ left: 140, right: 24, top: 16, bottom: 32 }}
            series={[{ data: d.by_site.map(s => s.count), label: 'Work orders', color: ACCENT.violet }]}
            yAxis={[{ data: d.by_site.map(s => s.site_name), scaleType: 'band' }]}
            grid={{ vertical: true }}
            sx={{ '& .MuiChartsAxis-line': { stroke: 'transparent' }, '& .MuiChartsAxis-tick': { stroke: 'transparent' } }}
          />
        </Section>
      )}
    </Stack>
  )
}

// ─── MTTR / MTBF ────────────────────────────────────────────────────────────

function MttrMtbfTab({ months }: { months: number }) {
  const q = useQuery({ queryKey: ['reports', 'mttr-mtbf', months], queryFn: () => api.reports.mttrMtbf({ months }) })
  const d: MttrMtbfData | undefined = q.data

  const computed = useMemo(() => {
    if (!d) return null
    const mttrSeries = d.mttr_trend.map(t => t.hours)
    const mtbfSeries = d.mtbf_trend.map(t => t.days)
    const closedToResolvedSeries = d.closed_to_resolved_trend.map(t => t.hours)
    return {
      mttrSeries, mtbfSeries, closedToResolvedSeries,
      mttrDelta: periodDelta(mttrSeries),
      mtbfDelta: periodDelta(mtbfSeries),
      closedToResolvedDelta: periodDelta(closedToResolvedSeries),
      reliabilityScore: d.mtbf_days > 0
        ? Math.min(100, Math.round((d.mtbf_days / (d.mtbf_days + d.mttr_hours / 24)) * 100))
        : 0,
    }
  }, [d])

  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  if (q.isLoading || !d || !computed) return <Loading />

  const insights: { tone: 'good' | 'warn' | 'info'; text: string }[] = []
  if (computed.mttrDelta != null && computed.mttrDelta < -5) {
    insights.push({ tone: 'good', text: `Mean time to repair has improved by ${Math.abs(computed.mttrDelta).toFixed(1)}% recently.` })
  } else if (computed.mttrDelta != null && computed.mttrDelta > 10) {
    insights.push({ tone: 'warn', text: `MTTR has climbed ${computed.mttrDelta.toFixed(1)}% — repairs are getting slower.` })
  }
  if (computed.mtbfDelta != null && computed.mtbfDelta > 5) {
    insights.push({ tone: 'good', text: `MTBF is up ${computed.mtbfDelta.toFixed(1)}% — assets are failing less often.` })
  }
  if (insights.length === 0) {
    insights.push({ tone: 'info', text: 'Reliability KPIs are tracking inside normal bounds.' })
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <HeroKpi label="Open → Closed" value={`${d.mttr_hours}h`} sub="MTTR — creation to close"
          accent={ACCENT.blue}    spark={computed.mttrSeries} deltaPct={computed.mttrDelta} deltaInversed
          icon={<SpeedOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Closed → Resolved" value={`${d.closed_to_resolved_hours}h`} sub="Close to reporter acknowledgement"
          accent={ACCENT.violet}  spark={computed.closedToResolvedSeries} deltaPct={computed.closedToResolvedDelta} deltaInversed
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="MTBF" value={`${d.mtbf_days}d`} sub="Mean time between failures"
          accent={ACCENT.green}   spark={computed.mtbfSeries} deltaPct={computed.mtbfDelta}
          icon={<TimerOutlinedIcon sx={{ fontSize: 16 }} />} />
        <Box sx={{
          flex: 1, minWidth: 200, position: 'relative', overflow: 'hidden',
          p: 2.25, borderRadius: 3, border: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Box sx={{ width: 96, height: 96, flexShrink: 0 }}>
            <Gauge value={computed.reliabilityScore} valueMin={0} valueMax={100}
              startAngle={-110} endAngle={110}
              text={({ value }) => `${value}%`}
              sx={{
                [`& .${gaugeClasses.valueText}`]: { fontSize: 18, fontWeight: 800 },
                [`& .${gaugeClasses.valueArc}`]: {
                  fill: computed.reliabilityScore >= 80 ? ACCENT.green
                      : computed.reliabilityScore >= 60 ? ACCENT.amber : ACCENT.red,
                },
                [`& .${gaugeClasses.referenceArc}`]: {
                  fill: 'rgba(148, 163, 184, 0.18)',
                },
              }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.secondary' }}>
              Reliability score
            </Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, mt: 0.5, color: 'text.primary' }}>
              {computed.reliabilityScore >= 80 ? 'Excellent' : computed.reliabilityScore >= 60 ? 'Good' : 'Needs attention'}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5, maxWidth: 200 }}>
              MTBF weighed against MTTR — higher is better.
            </Typography>
          </Box>
        </Box>
      </Stack>

      <InsightBar items={insights} />

      <Section title="MTTR trend" subtitle="Hours from work order creation to close" accent={ACCENT.blue}>
        {d.mttr_trend.length > 0 ? (
          <LineChart height={280}
            margin={{ left: 60, right: 16, top: 16, bottom: 40 }}
            series={[{
              data: computed.mttrSeries, label: 'MTTR (hours)', color: ACCENT.blue,
              showMark: true, area: true, curve: 'monotoneX',
              valueFormatter: (v) => v == null ? '' : `${v}h`,
            }]}
            xAxis={[{ data: d.mttr_trend.map(t => t.month), scaleType: 'band' }]}
            grid={{ horizontal: true }}
            sx={{
              '& .MuiAreaElement-root': { fillOpacity: 0.12 },
              '& .MuiChartsAxis-line': { stroke: 'transparent' },
            }}
          />
        ) : <EmptyState title="Not enough data" hint="Need at least a few completed reactive work orders for a trend line." />}
      </Section>

      <Section title="Closed → Resolved trend" subtitle="Hours from close to reporter acknowledgement" accent={ACCENT.violet}>
        {d.closed_to_resolved_trend.length > 0 ? (
          <LineChart height={280}
            margin={{ left: 60, right: 16, top: 16, bottom: 40 }}
            series={[{
              data: computed.closedToResolvedSeries, label: 'Closed → Resolved (hours)', color: ACCENT.violet,
              showMark: true, area: true, curve: 'monotoneX',
              valueFormatter: (v) => v == null ? '' : `${v}h`,
            }]}
            xAxis={[{ data: d.closed_to_resolved_trend.map(t => t.month), scaleType: 'band' }]}
            grid={{ horizontal: true }}
            sx={{
              '& .MuiAreaElement-root': { fillOpacity: 0.12 },
              '& .MuiChartsAxis-line': { stroke: 'transparent' },
            }}
          />
        ) : <EmptyState title="Not enough data" hint="Need at least a few resolved work orders for a trend line." />}
      </Section>

      <Section title="MTBF trend" subtitle="Days between failures per asset" accent={ACCENT.green}>
        {d.mtbf_trend.length > 0 ? (
          <LineChart height={280}
            margin={{ left: 60, right: 16, top: 16, bottom: 40 }}
            series={[{
              data: computed.mtbfSeries, label: 'MTBF (days)', color: ACCENT.green,
              showMark: true, area: true, curve: 'monotoneX',
              valueFormatter: (v) => v == null ? '' : `${v}d`,
            }]}
            xAxis={[{ data: d.mtbf_trend.map(t => t.month), scaleType: 'band' }]}
            grid={{ horizontal: true }}
            sx={{
              '& .MuiAreaElement-root': { fillOpacity: 0.12 },
              '& .MuiChartsAxis-line': { stroke: 'transparent' },
            }}
          />
        ) : <EmptyState title="Not enough data" hint="MTBF needs multiple failures recorded against the same asset." />}
      </Section>
    </Stack>
  )
}

// ─── PM Compliance ──────────────────────────────────────────────────────────

function PmComplianceTab({ months }: { months: number }) {
  const q = useQuery({ queryKey: ['reports', 'pm', months], queryFn: () => api.reports.pmCompliance({ months }) })
  const d: PmComplianceData | undefined = q.data

  const computed = useMemo(() => {
    if (!d) return null
    const onTimeSeries = d.by_month.map(m => m.on_time)
    const overdueSeries = d.by_month.map(m => Math.max(0, m.total - m.on_time))
    const compSeries = d.by_month.map(m => m.total > 0 ? Math.round((m.on_time / m.total) * 100) : 0)
    return {
      onTimeSeries, overdueSeries, compSeries,
      compDelta: periodDelta(compSeries),
    }
  }, [d])

  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  if (q.isLoading || !d || !computed) return <Loading />

  const compColor = d.compliance_pct >= 90 ? ACCENT.green : d.compliance_pct >= 70 ? ACCENT.amber : ACCENT.red

  const insights: { tone: 'good' | 'warn' | 'info'; text: string }[] = []
  if (d.compliance_pct >= 95) insights.push({ tone: 'good', text: 'Industry-leading PM execution — catching everything before it breaks.' })
  else if (d.compliance_pct >= 80) insights.push({ tone: 'info', text: 'PM compliance is healthy — close to industry benchmarks.' })
  else if (d.total > 0) insights.push({ tone: 'warn', text: `${d.overdue} preventive work orders are overdue. Consider redistributing PM load.` })
  if (computed.compDelta != null && computed.compDelta > 5) {
    insights.push({ tone: 'good', text: `PM compliance trending up ${computed.compDelta.toFixed(1)}% — keep it going.` })
  } else if (computed.compDelta != null && computed.compDelta < -5) {
    insights.push({ tone: 'warn', text: `PM compliance has dropped ${Math.abs(computed.compDelta).toFixed(1)}% recently.` })
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Box sx={{
          flex: 1.4, minWidth: 280, position: 'relative', overflow: 'hidden',
          p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 2.5,
          '&::before': {
            content: '""', position: 'absolute', inset: 0,
            background: `linear-gradient(135deg, ${compColor}15 0%, transparent 60%)`,
            pointerEvents: 'none',
          },
        }}>
          <Box sx={{ position: 'relative', width: 130, height: 130, flexShrink: 0 }}>
            <Gauge value={d.compliance_pct} valueMin={0} valueMax={100}
              startAngle={-110} endAngle={110}
              text={({ value }) => `${value}%`}
              sx={{
                [`& .${gaugeClasses.valueText}`]: { fontSize: 26, fontWeight: 800 },
                [`& .${gaugeClasses.valueArc}`]: { fill: compColor },
                [`& .${gaugeClasses.referenceArc}`]: { fill: 'rgba(148, 163, 184, 0.18)' },
              }} />
          </Box>
          <Box sx={{ position: 'relative' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.secondary' }}>
              PM Compliance
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 800, mt: 0.5 }}>
              {d.compliance_pct >= 90 ? 'Excellent' : d.compliance_pct >= 70 ? 'On track' : 'Action needed'}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.75, maxWidth: 260, lineHeight: 1.5 }}>
              Preventive work orders completed before their due date.
            </Typography>
          </Box>
        </Box>
        <HeroKpi label="On time"          value={d.on_time}  accent={ACCENT.green} deltaPct={null}
          icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Overdue"          value={d.overdue}  accent={ACCENT.red}   deltaPct={null}
          icon={<WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Total preventive" value={d.total}    accent={ACCENT.violet} deltaPct={null}
          icon={<BuildOutlinedIcon sx={{ fontSize: 16 }} />} />
      </Stack>

      <InsightBar items={insights} />

      <Section title="PM compliance by month" subtitle="Green = on time, red = overdue at close">
        {d.by_month.length > 0 ? (
          <BarChart height={320}
            margin={{ left: 50, right: 16, top: 16, bottom: 40 }}
            series={[
              { data: computed.onTimeSeries,  label: 'On time', color: ACCENT.green, stack: 'pm' },
              { data: computed.overdueSeries, label: 'Overdue', color: ACCENT.red,   stack: 'pm' },
            ]}
            xAxis={[{ data: d.by_month.map(m => m.month), scaleType: 'band' }]}
            grid={{ horizontal: true }}
            sx={{ '& .MuiChartsAxis-line': { stroke: 'transparent' }, '& .MuiChartsAxis-tick': { stroke: 'transparent' } }}
          />
        ) : <EmptyState title="No preventive maintenance data yet"
              hint="Set up recurring work order schedules to populate this chart." />}
      </Section>

      {computed.compSeries.length > 0 && (
        <Section title="Compliance % trend" subtitle="Higher is better — aim for the 90% line" accent={ACCENT.primary}>
          <LineChart height={250}
            margin={{ left: 60, right: 16, top: 16, bottom: 40 }}
            series={[{
              data: computed.compSeries, label: 'Compliance %', color: ACCENT.primary,
              showMark: true, area: true, curve: 'monotoneX',
              valueFormatter: (v) => v == null ? '' : `${v}%`,
            }]}
            xAxis={[{ data: d.by_month.map(m => m.month), scaleType: 'band' }]}
            yAxis={[{ min: 0, max: 100 }]}
            grid={{ horizontal: true }}
            sx={{ '& .MuiAreaElement-root': { fillOpacity: 0.14 }, '& .MuiChartsAxis-line': { stroke: 'transparent' } }}
          />
        </Section>
      )}
    </Stack>
  )
}

// ─── SLA Compliance ─────────────────────────────────────────────────────────

function SlaComplianceTab({ months }: { months: number }) {
  const q = useQuery({ queryKey: ['reports', 'sla', months], queryFn: () => api.reports.slaCompliance({ months }) })
  const d: SlaComplianceData | undefined = q.data

  const trendSeries = useMemo(() => d?.trend.map(t => t.compliance_pct) ?? [], [d])
  const trendDelta = periodDelta(trendSeries)

  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  if (q.isLoading || !d) return <Loading />

  const respColor = d.response_compliance_pct >= 90 ? ACCENT.green : d.response_compliance_pct >= 70 ? ACCENT.amber : ACCENT.red
  const resoColor = d.resolution_compliance_pct >= 90 ? ACCENT.green : d.resolution_compliance_pct >= 70 ? ACCENT.amber : ACCENT.red

  const breachPct = d.total_with_sla > 0 ? Math.round((d.breached / d.total_with_sla) * 100) : 0
  const insights: { tone: 'good' | 'warn' | 'info'; text: string }[] = []
  if (d.response_compliance_pct >= 95 && d.resolution_compliance_pct >= 95) {
    insights.push({ tone: 'good', text: 'Both response and resolution SLAs are above 95%. Customers are getting elite service.' })
  } else if (breachPct >= 15 && d.total_with_sla > 0) {
    insights.push({ tone: 'warn', text: `${breachPct}% of SLA-tracked work has breached. Review priority routing and assignee load.` })
  } else if (d.total_with_sla > 0) {
    insights.push({ tone: 'info', text: `${d.total_with_sla} work orders covered by SLA policies; ${d.breached} have breached.` })
  }
  if (trendDelta != null && trendDelta > 5) {
    insights.push({ tone: 'good', text: `SLA compliance has improved ${trendDelta.toFixed(1)}% recently.` })
  } else if (trendDelta != null && trendDelta < -5) {
    insights.push({ tone: 'warn', text: `SLA compliance has dropped ${Math.abs(trendDelta).toFixed(1)}% recently.` })
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <HeroKpi label="Response compliance"   value={`${d.response_compliance_pct}%`}   accent={respColor}
          deltaPct={null} icon={<TimerOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Resolution compliance" value={`${d.resolution_compliance_pct}%`} accent={resoColor}
          deltaPct={null} icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Total with SLA"        value={d.total_with_sla} accent={ACCENT.blue} deltaPct={null}
          icon={<AssignmentOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Breached"              value={d.breached} accent={ACCENT.red}
          sub={d.total_with_sla > 0 ? `${breachPct}% of tracked work` : undefined}
          spark={trendSeries.length > 0 ? trendSeries.map(v => 100 - v) : undefined}
          deltaPct={null} icon={<WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />} />
      </Stack>

      <InsightBar items={insights} />

      <Section title="Compliance by priority" subtitle="Critical work should hit 100% — degraded compliance there is the loudest signal.">
        {d.by_priority.length > 0 ? (
          <Stack spacing={1.5}>
            {d.by_priority.map((p) => {
              const tone = p.compliance_pct >= 90 ? ACCENT.green : p.compliance_pct >= 70 ? ACCENT.amber : ACCENT.red
              return (
                <Stack key={p.priority} direction="row" alignItems="center" spacing={2}>
                  <Chip label={p.priority} size="small" sx={{
                    width: 86, textTransform: 'capitalize', fontWeight: 700, fontSize: 11.5,
                    bgcolor: `${PRIORITY_COLORS[p.priority] ?? ACCENT.slate}18`,
                    color:   PRIORITY_COLORS[p.priority] ?? ACCENT.slate,
                  }} />
                  <Box sx={{ flex: 1, height: 10, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%', borderRadius: 99,
                      width: `${p.compliance_pct}%`,
                      background: `linear-gradient(90deg, ${tone}AA, ${tone})`,
                      transition: 'width 600ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }} />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, width: 56, textAlign: 'right', color: tone }}>
                    {p.compliance_pct}%
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', width: 100, textAlign: 'right' }}>
                    {p.breached}/{p.total} breached
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        ) : <EmptyState title="No SLA data yet" hint="Create SLA policies in Settings, then attach them to work orders." />}
      </Section>

      {d.trend.length > 0 && (
        <Section title="SLA compliance trend" subtitle="Aim to keep this above your contractual floor." accent={ACCENT.green}>
          <LineChart height={280}
            margin={{ left: 60, right: 16, top: 16, bottom: 40 }}
            series={[{
              data: trendSeries, label: 'Compliance %', color: ACCENT.green,
              showMark: true, area: true, curve: 'monotoneX',
              valueFormatter: (v) => v == null ? '' : `${v}%`,
            }]}
            xAxis={[{ data: d.trend.map(t => t.month), scaleType: 'band' }]}
            yAxis={[{ min: 0, max: 100 }]}
            grid={{ horizontal: true }}
            sx={{ '& .MuiAreaElement-root': { fillOpacity: 0.14 }, '& .MuiChartsAxis-line': { stroke: 'transparent' } }}
          />
        </Section>
      )}
    </Stack>
  )
}

// ─── Cost Analysis ──────────────────────────────────────────────────────────

function CostTab({ months, currency }: { months: number; currency: string }) {
  const q = useQuery({ queryKey: ['reports', 'cost', months], queryFn: () => api.reports.costAnalysis({ months }) })
  const d: CostAnalysisData | undefined = q.data

  const computed = useMemo(() => {
    if (!d) return null
    // Defensive null-guards so a malformed response (or org with no
    // cost data at all) doesn't throw mid-render and leave the page
    // wedged on the spinner.
    const byMonth   = Array.isArray(d.by_month)    ? d.by_month    : []
    const byAsset   = Array.isArray(d.by_asset)    ? d.by_asset    : []
    const partsSeries = byMonth.map(m => m.parts ?? 0)
    const laborSeries = byMonth.map(m => m.labor ?? 0)
    const totalSeries = byMonth.map(m => (m.parts ?? 0) + (m.labor ?? 0))
    return {
      partsSeries, laborSeries, totalSeries,
      totalDelta: periodDelta(totalSeries),
      partsDelta: periodDelta(partsSeries),
      laborDelta: periodDelta(laborSeries),
      topAsset: byAsset[0],
      // Cost concentration — if the top 3 assets make up >50% of spend
      // that's worth surfacing; usually they're the ones to upgrade.
      // Guard the divide-by-zero when total_cost is 0 (org without
      // any logged parts / hours yet).
      top3Share: (byAsset.length > 0 && d.total_cost > 0)
        ? byAsset.slice(0, 3).reduce((s, a) => s + (a.total ?? 0), 0) / d.total_cost
        : null,
    }
  }, [d])

  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  if (q.isLoading || !d || !computed) return <Loading />

  const fmt = (n: number) =>
    `${currency}${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`

  const insights: { tone: 'good' | 'warn' | 'info'; text: string }[] = []
  if (computed.totalDelta != null && computed.totalDelta > 15) {
    insights.push({ tone: 'warn', text: `Maintenance spend is up ${computed.totalDelta.toFixed(1)}% — review the top cost drivers.` })
  } else if (computed.totalDelta != null && computed.totalDelta < -10) {
    insights.push({ tone: 'good', text: `Maintenance spend has dropped ${Math.abs(computed.totalDelta).toFixed(1)}%.` })
  }
  if (computed.top3Share != null && computed.top3Share > 0.5) {
    insights.push({ tone: 'warn', text: `Top 3 assets account for ${Math.round(computed.top3Share * 100)}% of spend — consider replacement / refurbishment.` })
  }
  if (computed.topAsset) {
    insights.push({ tone: 'info', text: `Highest-cost asset: ${computed.topAsset.asset_name} at ${fmt(computed.topAsset.total)}.` })
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <HeroKpi label="Total spend"  value={fmt(d.total_cost)} accent={ACCENT.primary}
          spark={computed.totalSeries} deltaPct={computed.totalDelta} deltaInversed
          icon={<AttachMoneyOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Parts spend"  value={fmt(d.parts_cost)} accent={ACCENT.violet}
          spark={computed.partsSeries} deltaPct={computed.partsDelta} deltaInversed
          icon={<BuildOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Labour spend" value={fmt(d.labor_cost)} accent={ACCENT.blue}
          sub="From estimated hours"
          spark={computed.laborSeries} deltaPct={computed.laborDelta} deltaInversed
          icon={<TimerOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Avg / month"  value={fmt(d.by_month.length > 0 ? d.total_cost / d.by_month.length : 0)}
          accent={ACCENT.amber} sub="trailing window" deltaPct={null}
          icon={<TrendingUpOutlinedIcon sx={{ fontSize: 16 }} />} />
      </Stack>

      <InsightBar items={insights} />

      <Section title="Cost by month" subtitle="Stacked parts vs labour over the trailing window">
        {d.by_month.length > 0 ? (
          <BarChart height={320}
            margin={{ left: 70, right: 16, top: 16, bottom: 40 }}
            series={[
              { data: computed.partsSeries, label: 'Parts',  color: ACCENT.violet, stack: 'cost', valueFormatter: (v: number | null) => v == null ? '' : fmt(v) },
              { data: computed.laborSeries, label: 'Labour', color: ACCENT.blue,   stack: 'cost', valueFormatter: (v: number | null) => v == null ? '' : fmt(v) },
            ]}
            xAxis={[{ data: d.by_month.map(m => m.month), scaleType: 'band' }]}
            grid={{ horizontal: true }}
            sx={{ '& .MuiChartsAxis-line': { stroke: 'transparent' }, '& .MuiChartsAxis-tick': { stroke: 'transparent' } }}
          />
        ) : <EmptyState title="No cost data yet" hint="Spend will appear once work orders log parts or hours." />}
      </Section>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
        {d.by_asset.length > 0 && (
          <Box sx={{ flex: 1.4, minWidth: 380 }}>
            <Section title="Top assets by cost" subtitle="The 10 assets consuming the most maintenance budget"
              action={<Chip size="small" label={`${d.by_asset.length} assets`}
                sx={{ height: 22, fontSize: 11, fontWeight: 700 }} />}>
              <Stack>
                {d.by_asset.slice(0, 10).map((a, i) => {
                  const maxT = d.by_asset[0]?.total ?? 0
                  const pct = maxT > 0 ? (a.total / maxT) * 100 : 0
                  return (
                    <Box key={a.asset_id}
                      sx={{
                        py: 1.5, px: 1, borderRadius: 1.5,
                        ...(i !== Math.min(d.by_asset.length, 10) - 1 && {
                          borderBottom: '1px solid', borderColor: 'divider',
                        }),
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 150ms ease',
                      }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.75 }}>
                        <Chip label={`#${i + 1}`} size="small" sx={{
                          height: 20, fontSize: 10.5, fontWeight: 800,
                          bgcolor: i === 0 ? `${ACCENT.red}1A` : 'action.hover',
                          color:   i === 0 ? ACCENT.red       : 'text.secondary',
                          minWidth: 32,
                        }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }} noWrap>{a.asset_name}</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.disabled', fontFamily: 'ui-monospace, monospace' }}>{a.asset_code}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary' }}>
                          {fmt(a.total)}
                        </Typography>
                      </Stack>
                      <Box sx={{ height: 6, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden' }}>
                        <Box sx={{
                          height: '100%', width: `${pct}%`,
                          background: `linear-gradient(90deg, ${ACCENT.violet}, ${ACCENT.blue})`,
                          transition: 'width 600ms cubic-bezier(0.4, 0, 0.2, 1)',
                        }} />
                      </Box>
                      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                        <Typography sx={{ fontSize: 11, color: ACCENT.violet, fontWeight: 600 }}>
                          Parts {fmt(a.parts)}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: ACCENT.blue, fontWeight: 600 }}>
                          Labour {fmt(a.labor)}
                        </Typography>
                      </Stack>
                    </Box>
                  )
                })}
              </Stack>
            </Section>
          </Box>
        )}

        {d.by_category.length > 0 && (
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Section title="Cost by category" subtitle="Where the money goes">
              <PieChart height={260}
                series={[{
                  innerRadius: 60, outerRadius: 100, paddingAngle: 2, cornerRadius: 6,
                  data: d.by_category.map((c, i) => ({ id: i, value: c.total, label: c.category })),
                  highlightScope: { fade: 'global', highlight: 'item' },
                }]}
                slotProps={{ legend: { direction: 'horizontal', position: { vertical: 'bottom', horizontal: 'center' } } }}
              />
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={0.75}>
                {d.by_category.slice(0, 5).map((c) => (
                  <Stack key={c.category} direction="row" justifyContent="space-between">
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'capitalize' }}>{c.category}</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{fmt(c.total)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Section>
          </Box>
        )}
      </Stack>
    </Stack>
  )
}

// ─── Backlog ────────────────────────────────────────────────────────────────

function BacklogTab() {
  const q = useQuery({ queryKey: ['reports', 'backlog'], queryFn: () => api.reports.backlog() })
  const d: BacklogData | undefined = q.data

  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  if (q.isLoading || !d) return <Loading />

  const insights: { tone: 'good' | 'warn' | 'info'; text: string }[] = []
  if (d.open_count === 0) {
    insights.push({ tone: 'good', text: 'Zero open work orders. Backlog cleared.' })
  } else if (d.avg_age_days > 30) {
    insights.push({ tone: 'warn', text: `Average backlog age is ${d.avg_age_days} days — work is sitting too long before being addressed.` })
  } else if (d.avg_age_days <= 7) {
    insights.push({ tone: 'good', text: `Backlog is fresh — average age just ${d.avg_age_days} days.` })
  }
  const criticalAge = d.by_priority.find(p => p.priority === 'critical')?.avg_age_days
  if (criticalAge != null && criticalAge > 3) {
    insights.push({ tone: 'warn', text: `Critical work has been sitting ${criticalAge} days on average — escalate these.` })
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <HeroKpi label="Open work orders" value={d.open_count} accent={ACCENT.amber} deltaPct={null}
          icon={<AssignmentOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Average age" value={`${d.avg_age_days}d`} sub="days since creation"
          accent={ACCENT.blue} deltaPct={null}
          icon={<TimerOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Oldest critical"
          value={`${d.by_priority.find(p => p.priority === 'critical')?.avg_age_days ?? 0}d`}
          sub="avg age in critical bucket"
          accent={ACCENT.red} deltaPct={null}
          icon={<WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />} />
        <HeroKpi label="Aging > 30d"
          value={d.aging.filter(a => /30|60|90|180|365|year/i.test(a.bucket)).reduce((s, a) => s + a.count, 0)}
          sub="entries in old buckets" accent={ACCENT.violet} deltaPct={null}
          icon={<ArrowOutwardOutlinedIcon sx={{ fontSize: 16 }} />} />
      </Stack>

      <InsightBar items={insights} />

      <Section title="Aging buckets" subtitle="How long open work orders have been sitting">
        {d.aging.length > 0 ? (
          <BarChart height={300}
            margin={{ left: 50, right: 16, top: 16, bottom: 40 }}
            series={[{ data: d.aging.map(a => a.count), label: 'Work orders', color: ACCENT.amber }]}
            xAxis={[{ data: d.aging.map(a => a.bucket), scaleType: 'band' }]}
            grid={{ horizontal: true }}
            sx={{ '& .MuiChartsAxis-line': { stroke: 'transparent' }, '& .MuiChartsAxis-tick': { stroke: 'transparent' } }}
          />
        ) : <EmptyState title="No open work orders" hint="Backlog charts populate when there's pending work." />}
      </Section>

      {d.by_priority.length > 0 && (
        <Section title="Backlog by priority" subtitle="Critical and high should always be the smallest buckets">
          <Stack spacing={1.5}>
            {d.by_priority.map((p) => {
              const tone = PRIORITY_COLORS[p.priority] ?? ACCENT.slate
              const maxCount = Math.max(...d.by_priority.map(x => x.count), 1)
              const pct = (p.count / maxCount) * 100
              return (
                <Stack key={p.priority} direction="row" alignItems="center" spacing={2}>
                  <Chip label={p.priority} size="small" sx={{
                    width: 86, textTransform: 'capitalize', fontWeight: 700, fontSize: 11.5,
                    bgcolor: `${tone}18`, color: tone,
                  }} />
                  <Box sx={{ flex: 1, height: 10, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%', width: `${pct}%`, borderRadius: 99,
                      background: `linear-gradient(90deg, ${tone}99, ${tone})`,
                      transition: 'width 600ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }} />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, width: 50, textAlign: 'right' }}>{p.count}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', width: 130, textAlign: 'right' }}>
                    avg {p.avg_age_days} days old
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        </Section>
      )}
    </Stack>
  )
}
